// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import { formatNumber } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Types --------------------------------------------------------------------------------------
export type TyAttentionItem = { key: string; label: string; count: number; to: string; tone: "warn" | "bad" };

type Props = { items: TyAttentionItem[] };
// --- Logic --------------------------------------------------------------------------------------
/**
 * The first thing on the first screen: what is waiting on Lozicore right now. A dashboard that reports
 * health without surfacing the work is a report, not a tool.
 *
 * **An entry at zero is not drawn.** A row of reassuring zeroes teaches an admin to stop reading the
 * row at all, which is exactly when the one non-zero day arrives — so when everything is clear the
 * panel says so in one line and takes no more space than that.
 */
export default function AttentionPanel({ items }: Props) {
    const t = useTranslation();
    const navigate = useNavigate();
    const live = items.filter((item) => item.count > 0);

    if (!live.length)
        return (
            <section className="attention attention--clear">
                <Icon name="check" size={18} />
                <p className="attention__clear-text">{t.home.attentionClear}</p>
            </section>
        );

    return (
        <section className="attention">
            <p className="attention__title">
                <Icon name="alert" size={16} />
                <span>{t.home.attentionTitle}</span>
            </p>

            <div className="attention__items">
                {live.map((item) => (
                    <button key={item.key} type="button" className="attention__item" data-tone={item.tone} onClick={() => navigate(item.to)}>
                        <span className="attention__count">{formatNumber(item.count)}</span>
                        <span className="attention__label">{item.label}</span>
                    </button>
                ))}
            </div>
        </section>
    );
}
